// ─────────────────────────────────────────────
// Mapeo vista interna del monolito ↔ ruta URL pública.
//
// VIEW_ROUTES  → vistas públicas e indexables (landings, páginas de empresa).
// APP_ROUTES   → apps de producto privadas: tienen URL para UX (recargar,
//                favoritos) pero NO se indexan (ver robots.txt + meta noindex).
// ─────────────────────────────────────────────

export const VIEW_ROUTES = {
  'web-development':     '/desarrolloweb',
  'nexusstock-landing':  '/nexusstock',
  'nexusorders-landing': '/nexusorders',
  'nexushub-landing':    '/nexushub',
  'condominios-landing': '/condominios',
  'solar-ai-landing':    '/gestorclientes',
  'nexuscommerce-landing': '/nexuscommerce',
  'nexustour-landing':     '/nexustour',
  'productos':           '/productos',
  'about':               '/nosotros',
  'team':                '/equipo',
  'blog':                '/blog',
  'courses':             '/cursos',
  'tutorials':           '/tutoriales',
  'help-center':         '/ayuda',
};

// Apps de producto (privadas, requieren sesión).
export const APP_ROUTES = {
  'stock':          '/app/nexusstock',
  'orders':         '/app/nexusorders',
  'messages':       '/app/nexushub',
  'gestorclientes': '/app/gestorclientes',
  'condominios':    '/app/condominios',
};

// Si alguien abre una /app/* sin sesión → se le manda a esta landing pública.
export const APP_FALLBACK_LANDING = {
  '/app/nexusstock':     '/nexusstock',
  '/app/nexusorders':    '/nexusorders',
  '/app/nexushub':       '/nexushub',
  '/app/gestorclientes': '/gestorclientes',
  '/app/condominios':    '/condominios',
};

// Combinado: cualquier vista con ruta (pública o app).
export const ALL_ROUTES = { ...VIEW_ROUTES, ...APP_ROUTES };

// Inversos: ruta → vista.
export const ROUTE_VIEWS = Object.fromEntries(
  Object.entries(VIEW_ROUTES).map(([view, route]) => [route, view])
);
export const APP_ROUTE_VIEWS = Object.fromEntries(
  Object.entries(APP_ROUTES).map(([view, route]) => [route, view])
);
export const ALL_ROUTE_VIEWS = { ...ROUTE_VIEWS, ...APP_ROUTE_VIEWS };

// Metadata SEO de las rutas públicas (title, description, keywords).
const BASE = 'https://nexusai.company';
export const ROUTE_META = {
  '/desarrolloweb': {
    title: 'Desarrollo Web Profesional - NexusAI',
    description: 'Creamos sitios web y tiendas online profesionales para negocios bolivianos. Diseño moderno, hosting incluido y soporte continuo.',
    keywords: 'desarrollo web, diseño web, páginas web Bolivia, tiendas online, hosting',
  },
  '/nexusstock': {
    title: 'Inventario IA - Sistema de Inventario Inteligente - NexusAI',
    description: 'Controla tu inventario con predicción de demanda y optimización automática. Ideal para tiendas, ferreterías y farmacias en Bolivia.',
    keywords: 'inventario, control de stock, gestión de inventario, NexusStock, IA inventario Bolivia',
  },
  '/nexusorders': {
    title: 'Pedidos IA - Sistema de Pedidos para Restaurantes - NexusAI',
    description: 'Gestión integral de pedidos multicanal para restaurantes, pizzerías y cafeterías. Pedidos, delivery y cocina en un solo sistema.',
    keywords: 'pedidos restaurante, sistema restaurante, delivery, NexusOrders, software restaurante Bolivia',
  },
  '/nexushub': {
    title: 'Mensajes IA - Mensajería Social con IA - NexusAI',
    description: 'Centraliza WhatsApp, Instagram y Facebook con automatización avanzada de IA. Atiende a tus clientes desde un solo lugar.',
    keywords: 'mensajería social, WhatsApp business, automatización, NexusHUB, chatbot Bolivia',
  },
  '/condominios': {
    title: 'Condominios IA - Administración de Condominios - NexusAI',
    description: 'Administra residentes, pagos, reservas y comunicación de tu condominio o edificio de forma simple y profesional.',
    keywords: 'administración condominios, gestión edificios, expensas, residentes, NexusCondominios',
  },
  '/gestorclientes': {
    title: 'Clientes IA - CRM Inteligente - NexusAI',
    description: 'CRM para gestión de clientes y relaciones comerciales. Perfecto para peluquerías, veterinarias, gimnasios y clínicas.',
    keywords: 'CRM, gestión de clientes, agenda de citas, GestorClientes, software clientes Bolivia',
  },
  '/nexuscommerce': {
    title: 'Tienda IA - Ventas con IA por WhatsApp - NexusAI',
    description: 'Tu tienda atiende sola 24/7. Un agente de IA responde, recomienda productos y cierra ventas por WhatsApp mientras tú descansas.',
    keywords: 'ventas WhatsApp, agente IA, tienda online, NexusCommerce, vender por WhatsApp Bolivia',
  },
  '/nexustour': {
    title: 'Tours 3D - Tours 3D Inmobiliarios con IA - NexusAI',
    description: 'Convierte el video de una propiedad en un tour 3D navegable. Ideal para inmobiliarias y agentes que quieren vender más rápido.',
    keywords: 'tours 3D, recorridos virtuales, inmobiliaria, NexusTour, tours inmobiliarios Bolivia',
  },
  '/productos': {
    title: 'Productos - Soluciones de IA para Empresas - NexusAI',
    description: 'Conoce todos los productos NexusAI: inventario, pedidos, CRM, mensajería, condominios y más. Una solución para cada negocio.',
    keywords: 'productos NexusAI, software empresarial, soluciones IA, SaaS Bolivia',
  },
  '/nosotros': {
    title: 'Sobre Nosotros - NexusAI',
    description: 'Conoce a NexusAI: una empresa boliviana que transforma negocios con inteligencia artificial y soluciones de automatización.',
    keywords: 'sobre NexusAI, empresa de IA Bolivia, quiénes somos',
  },
  '/equipo': {
    title: 'Nuestro Equipo - NexusAI',
    description: 'Conoce al equipo detrás de NexusAI, expertos en inteligencia artificial y desarrollo de software para empresas bolivianas.',
    keywords: 'equipo NexusAI, expertos IA, talento Bolivia',
  },
  '/blog': {
    title: 'Blog - NexusAI',
    description: 'Artículos, guías y novedades sobre inteligencia artificial, automatización y transformación digital para negocios.',
    keywords: 'blog NexusAI, inteligencia artificial, automatización, transformación digital',
  },
  '/cursos': {
    title: 'Cursos y Capacitación - NexusAI',
    description: 'Capacítate en inteligencia artificial y herramientas digitales con los cursos de NexusAI.',
    keywords: 'cursos IA, capacitación, formación digital, cursos NexusAI',
  },
  '/tutoriales': {
    title: 'Tutoriales - NexusAI',
    description: 'Aprende a sacar el máximo provecho de los productos NexusAI con nuestros tutoriales paso a paso.',
    keywords: 'tutoriales NexusAI, guías, ayuda, cómo usar',
  },
  '/ayuda': {
    title: 'Centro de Ayuda - NexusAI',
    description: 'Encuentra respuestas, guías y soporte para todos los productos de NexusAI.',
    keywords: 'centro de ayuda, soporte NexusAI, preguntas frecuentes',
  },
};

Object.keys(ROUTE_META).forEach((route) => {
  ROUTE_META[route].ogUrl = `${BASE}${route}`;
});

// Metadata de las apps privadas — solo el title (para la pestaña/favorito).
// No llevan description SEO: van con noindex.
export const APP_META = {
  '/app/nexusstock':     { title: 'NexusStock - Mi Inventario - NexusAI' },
  '/app/nexusorders':    { title: 'NexusOrders - Mis Pedidos - NexusAI' },
  '/app/nexushub':       { title: 'NexusHUB - Mensajería - NexusAI' },
  '/app/gestorclientes': { title: 'GestorClientes - Mi CRM - NexusAI' },
  '/app/condominios':    { title: 'NexusCondominios - Administración - NexusAI' },
};
